import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { hojeBR } from '../../utils/data';
import pedirSenhaAdmin from '../common/pedirSenhaAdmin';
import { calcularMultaJurosAtraso } from '../../utils/multaJurosAtraso';
import s from '../../pages/PedidosOrcamentos.module.css';

const fmtMoeda = v => Number(v||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'});
const hoje     = () => hojeBR();

const FORMAS_RECEBIMENTO = ['Dinheiro','PIX','Cartão Débito','Cartão Crédito','Transferência','Cheque','Outro'];

export default function ModalReceber({ conta, contas, bandeiras = [], orgData, onReceber, onClose }) {
  const [f, setF] = useState({
    data_recebimento:  hoje(),
    valor_recebido:    conta.valor,
    conta_id:          conta.conta_id || '',
    forma_recebimento: 'Dinheiro',
    bandeira_id:       '',
    parcelas:          1,
    desconto:          '',
    juros:             '',
    multa:             '',
  });
  const [usarAjuste, setUsarAjuste] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [creditoTotal, setCreditoTotal] = useState(0);
  // Multa/juros por atraso vêm calculados automaticamente (Parâmetros →
  // Rotinas) e ficam bloqueados pra edição — alterar exige senha de
  // administrador ou gerente (o backend confere de novo, não confia só nisto).
  const [encargoBloqueado, setEncargoBloqueado] = useState(true);
  // "Pagar sem multa/juros": zera os dois campos e trava neles enquanto
  // marcado (some se a data de recebimento mudar e recalcular a sugestão,
  // igual ao comportamento padrão de encargoBloqueado — o usuário decide de
  // novo se ainda quer isentar pra essa nova data).
  const [semEncargoConfirmado, setSemEncargoConfirmado] = useState(false);
  const [senhaAjuste, setSenhaAjuste] = useState(null);
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  const sugestao = !conta.data_vencimento ? { multa: 0, juros: 0, diasAtraso: 0 }
    : calcularMultaJurosAtraso(conta.valor, conta.data_vencimento, f.data_recebimento, orgData);
  const temSugestao = sugestao.multa > 0.004 || sugestao.juros > 0.004;

  // Preenche automaticamente enquanto bloqueado — some se a data de
  // recebimento mudar (dias de atraso recalculam) e o campo ainda não foi
  // destravado por senha.
  useEffect(() => {
    if (!encargoBloqueado) return;
    if (semEncargoConfirmado) {
      setF(p => ({ ...p, multa: '0.00', juros: '0.00' }));
      return;
    }
    if (temSugestao) {
      setUsarAjuste(true);
      setF(p => ({ ...p, multa: sugestao.multa.toFixed(2), juros: sugestao.juros.toFixed(2) }));
    } else {
      setF(p => ({ ...p, multa: '', juros: '' }));
    }
  }, [sugestao.multa, sugestao.juros, encargoBloqueado, semEncargoConfirmado]); // eslint-disable-line

  const destravarEncargo = async () => {
    const senha = await pedirSenhaAdmin(
      temSugestao
        ? 'Alterar a multa/juros calculados automaticamente exige senha de administrador ou gerente.'
        : 'Informar multa/juros negociados exige senha de administrador ou gerente.',
      { acao: 'alterar_multa_juros_atraso', detalhes: `${conta.descricao} — sugerido: multa ${fmtMoeda(sugestao.multa)}, juros ${fmtMoeda(sugestao.juros)}` }
    );
    if (senha === null) return;
    setSenhaAjuste(senha);
    setEncargoBloqueado(false);
  };

  const alternarSemEncargo = async (marcar) => {
    if (!marcar) { setSemEncargoConfirmado(false); return; }
    const senha = await pedirSenhaAdmin(
      'Pagar sem multa/juros exige senha de administrador ou gerente.',
      { acao: 'pagar_sem_multa_juros', detalhes: `${conta.descricao} — sugerido: multa ${fmtMoeda(sugestao.multa)}, juros ${fmtMoeda(sugestao.juros)}` }
    );
    if (senha === null) return;
    setSenhaAjuste(senha);
    setSemEncargoConfirmado(true);
    setF(p => ({ ...p, multa: '0.00', juros: '0.00' }));
  };

  useEffect(() => {
    if (!conta.cliente_id || conta.cliente_id === 1) return;
    api.get('/financeiro/creditos-cliente', { params: { cliente_id: conta.cliente_id } })
      .then(r => setCreditoTotal(r.data?.total || 0))
      .catch(() => {});
  }, [conta.cliente_id]);

  const vTotal   = parseFloat(conta.valor || 0);
  const ehCartao       = f.forma_recebimento.startsWith('Cartão');
  const ehCredito      = f.forma_recebimento === 'Cartão Crédito';
  const ehCreditoCli   = f.forma_recebimento === 'Crédito do Cliente';
  // Desconto/juros negociado: quita o título inteiro pelo líquido calculado
  // (não combina com cartão/crédito do cliente, nem com recebimento parcial).
  // "Pagar sem multa/juros" (semEncargoConfirmado) é a exceção: título vencido
  // mas SEM cobrar o encargo agora — nesse caso não há ajuste nenhum de
  // verdade (desconto/juros/multa ficam todos zerados), então o valor
  // recebido pode ser parcial igual a um título em dia; o que ficar em
  // aberto continua vencido a partir da MESMA data original e volta a
  // calcular multa/juros normalmente da próxima vez.
  const podeAjuste = usarAjuste && !ehCartao && !ehCreditoCli && !semEncargoConfirmado;
  const vDesconto  = podeAjuste ? (parseFloat(f.desconto) || 0) : 0;
  const vJuros     = podeAjuste ? (parseFloat(f.juros) || 0) : 0;
  const vMulta     = podeAjuste ? (parseFloat(f.multa) || 0) : 0;
  // ISS retido pelo tomador na NFS-e: título com valor bruto, recebe o
  // líquido e quita inteiro (a diferença não é desconto).
  const vIssRet    = parseFloat(conta.iss_retido || 0);
  const comIss     = vIssRet > 0.004;
  const fixo       = podeAjuste || comIss;
  const vLiquido   = Math.max(0, Math.round((vTotal - vIssRet - vDesconto + vJuros + vMulta) * 100) / 100);
  const vReceb     = fixo ? vLiquido : parseFloat(f.valor_recebido || 0);
  const vRest      = fixo ? 0 : Math.max(0, Math.round((vTotal - vReceb) * 100) / 100);
  const parcial    = !fixo && vRest > 0.009;
  // PIX e transferência entram direto em conta bancária
  const soBanco        = ['PIX', 'Transferência'].includes(f.forma_recebimento);
  const contasFiltradas = soBanco ? contas.filter(c => c.tipo === 'banco') : contas;
  const temCreditoCli  = creditoTotal > 0.004;
  const bandeirasFiltradas = bandeiras.filter(b => {
    const t = b.tipo || 'ambos';
    if (f.forma_recebimento === 'Cartão Débito')  return t !== 'credito';
    if (ehCredito) return t !== 'debito';
    return true;
  });

  const trocarForma = (forma) => {
    // Conta selecionada deixa de valer se a nova forma exige banco
    const exigeBanco = ['PIX', 'Transferência'].includes(forma);
    setF(p => ({ ...p,
                 conta_id: exigeBanco && p.conta_id &&
                   contas.find(c => String(c.id) === String(p.conta_id))?.tipo !== 'banco'
                   ? '' : p.conta_id,
                 forma_recebimento: forma, bandeira_id: '', parcelas: 1,
                 valor_recebido:
                   forma.startsWith('Cartão') ? conta.valor
                   // Crédito menor que a conta: já sugere o máximo utilizável (gera restante)
                   : forma === 'Crédito do Cliente'
                     ? Math.min(parseFloat(conta.valor || 0), creditoTotal).toFixed(2)
                   : p.valor_recebido }));
  };

  const confirmar = async () => {
    if (ehCartao) {
      if (!f.bandeira_id) return alert('Selecione a bandeira do cartão.');
    } else if (ehCreditoCli) {
      if (vReceb <= 0) return alert('Informe um valor válido.');
      if (vReceb > creditoTotal + 0.004)
        return alert(`Crédito insuficiente: disponível ${fmtMoeda(creditoTotal)}.`);
    } else {
      if (!f.conta_id) return alert('Selecione a conta financeira.');
      if (vReceb <= 0) return alert('Informe um valor válido.');
    }
    if (podeAjuste && !semEncargoConfirmado && vDesconto <= 0.004 && vJuros <= 0.004 && vMulta <= 0.004)
      return alert('Informe um valor de desconto, juros ou multa — ou desmarque o ajuste.');
    setSalvando(true);
    try {
      await onReceber({
        ...f, desconto: vDesconto, juros: vJuros, multa: vMulta,
        senha_ajuste_manual: senhaAjuste || undefined,
      });
    } finally { setSalvando(false); }
  };

  return (
    <div className={s.confirmOverlay}>
      <div className={s.confirmBox} style={{width:440}}>
        <div className={s.confirmTitulo}>Confirmar Recebimento</div>
        <div className={s.confirmCorpo}>
          <p style={{marginBottom:12}}><strong>{conta.descricao}</strong></p>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12}}>
              Forma de Recebimento *
              <select className={s.selectErp} value={f.forma_recebimento}
                onChange={e=>trocarForma(e.target.value)}>
                {FORMAS_RECEBIMENTO.map(fp=><option key={fp} value={fp}>{fp}</option>)}
                {temCreditoCli && (
                  <option value="Crédito do Cliente">
                    💳 Crédito do Cliente — disponível {fmtMoeda(creditoTotal)}
                  </option>
                )}
              </select>
            </label>
            {ehCartao && (
              <div style={{display:'flex',gap:8}}>
                <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,flex:1,minWidth:0}}>
                  Bandeira *
                  <select className={s.selectErp} value={f.bandeira_id}
                    onChange={e=>set('bandeira_id',e.target.value)}>
                    <option value="">— Selecione —</option>
                    {bandeirasFiltradas.map(b=><option key={b.id} value={b.id}>{b.nome}</option>)}
                  </select>
                </label>
                {ehCredito && (
                  <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,width:90}}>
                    Parcelas
                    <select className={s.selectErp} value={f.parcelas}
                      onChange={e=>set('parcelas',parseInt(e.target.value))}>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(n=><option key={n} value={n}>{n}x</option>)}
                    </select>
                  </label>
                )}
              </div>
            )}
            {!ehCartao && (
              <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12}}>
                Data do Recebimento
                <input type="date" className={s.inputErp} value={f.data_recebimento}
                  onChange={e=>set('data_recebimento',e.target.value)} />
              </label>
            )}
            <div style={{display:'flex',gap:8}}>
              <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,flex:1,minWidth:0}}>
                Valor Total
                <input className={s.inputErp} readOnly value={fmtMoeda(vTotal)}
                  style={{background:'#f3f4f6',fontWeight:600,textAlign:'right'}} />
              </label>
              <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,flex:1,minWidth:0}}>
                Valor Recebido {!fixo && '*'}
                <input type="number" step="0.01" min="0.01" max={vTotal} className={s.inputErp}
                  value={fixo ? vLiquido.toFixed(2) : f.valor_recebido}
                  onChange={e=>set('valor_recebido',e.target.value)}
                  readOnly={ehCartao || fixo}
                  style={{fontWeight:600,textAlign:'right',
                    background: (ehCartao || fixo) ? '#f3f4f6' : '',
                    borderColor: parcial && !ehCartao ? '#f59e0b':''}} />
              </label>
            </div>
            {comIss && (
              <div data-testid="aviso-iss-retido" style={{fontSize:12,color:'#1e3a8a',background:'#eff6ff',
                border:'1px solid #bfdbfe',borderRadius:4,padding:'6px 8px'}}>
                ISS retido pelo tomador na NFS-e: <strong>{fmtMoeda(vIssRet)}</strong> — o cliente paga
                o líquido de <strong>{fmtMoeda(vTotal - vIssRet)}</strong>. O título continua com o valor
                bruto (receita do serviço); a retenção não é desconto.
              </div>
            )}
            {!ehCartao && !ehCreditoCli && (
              <div style={{border:'1px solid #e5e7eb',borderRadius:4,padding:'8px 12px'}}>
                {temSugestao ? (
                  <div style={{fontSize:12,color:'#92400e',fontWeight:600}}>
                    ⚠️ Título vencido há {sugestao.diasAtraso} dia{sugestao.diasAtraso>1?'s':''} —{' '}
                    {semEncargoConfirmado
                      ? 'recebendo sem multa/juros (pode ser parcial); o que ficar em aberto volta a calcular encargo depois'
                      : 'multa/juros aplicados automaticamente (Parâmetros → Rotinas)'}
                  </div>
                ) : (
                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
                    <input type="checkbox" checked={usarAjuste}
                      onChange={e=>{ setUsarAjuste(e.target.checked); set('desconto',''); set('juros',''); set('multa',''); }} />
                    Negociar desconto ou juros (quita o título inteiro pelo valor líquido)
                  </label>
                )}
                {temSugestao && encargoBloqueado && (
                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer',marginTop:6}}>
                    <input type="checkbox" checked={semEncargoConfirmado}
                      onChange={e=>alternarSemEncargo(e.target.checked)} />
                    Pagar sem multa/juros (exige senha de admin/gerente)
                  </label>
                )}
                {usarAjuste && (
                  <div style={{display:'flex',gap:8,marginTop:8}}>
                    <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,flex:1,minWidth:0}}>
                      Desconto R$
                      <input type="number" step="0.01" min="0" className={s.inputErp}
                        value={f.desconto} onChange={e=>set('desconto',e.target.value)}
                        placeholder="0,00" style={{textAlign:'right',width:'100%',boxSizing:'border-box'}} />
                    </label>
                    <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,flex:1,minWidth:0}}>
                      Multa R$
                      <input type="number" step="0.01" min="0" className={s.inputErp}
                        value={f.multa} onChange={e=>set('multa',e.target.value)}
                        readOnly={encargoBloqueado} placeholder="0,00"
                        style={{textAlign:'right', width:'100%', boxSizing:'border-box', background: encargoBloqueado ? '#f3f4f6' : ''}} />
                    </label>
                    <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,flex:1,minWidth:0}}>
                      Juros R$
                      <input type="number" step="0.01" min="0" className={s.inputErp}
                        value={f.juros} onChange={e=>set('juros',e.target.value)}
                        readOnly={encargoBloqueado} placeholder="0,00"
                        style={{textAlign:'right', width:'100%', boxSizing:'border-box', background: encargoBloqueado ? '#f3f4f6' : ''}} />
                    </label>
                  </div>
                )}
                {usarAjuste && encargoBloqueado && (
                  <button type="button" onClick={destravarEncargo}
                    style={{marginTop:6,fontSize:11,border:'1px solid #94a3b8',borderRadius:4,
                      background:'#fff',padding:'2px 8px',cursor:'pointer'}}>
                    ✏️ {temSugestao ? 'Alterar' : 'Informar'} multa/juros (exige senha de admin/gerente)
                  </button>
                )}
                {podeAjuste && (
                  <div style={{fontSize:12,color:'#166534',marginTop:6}}>
                    Valor líquido a receber: <strong>{fmtMoeda(vLiquido)}</strong>
                    {semEncargoConfirmado && <> — sem multa/juros, título quitado só pelo valor nominal</>}
                    {vDesconto > 0.004 && <> — desconto de {fmtMoeda(vDesconto)} lançado no DRE</>}
                    {(vMulta > 0.004 || vJuros > 0.004) && <> — </>}
                    {vMulta > 0.004 && <>multa de {fmtMoeda(vMulta)}</>}
                    {vMulta > 0.004 && vJuros > 0.004 && <> + </>}
                    {vJuros > 0.004 && <>juros de {fmtMoeda(vJuros)}</>}
                    {(vMulta > 0.004 || vJuros > 0.004) && <> lançados como receita financeira</>}
                  </div>
                )}
              </div>
            )}
            {parcial && !ehCartao && (
              <div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:4,
                padding:'8px 12px',fontSize:12,color:'#92400e'}}>
                ⚠️ Recebimento parcial — será gerada automaticamente uma nova conta em aberto de{' '}
                <strong>{fmtMoeda(vRest)}</strong> para o restante.
              </div>
            )}
            {ehCreditoCli && (
              <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:4,
                padding:'8px 12px',fontSize:12,color:'#166534'}}>
                💳 Será abatido do <strong>crédito do cliente</strong> (disponível: {fmtMoeda(creditoTotal)}).
                Não movimenta nenhuma conta financeira.
              </div>
            )}
            {ehCartao ? (
              <div style={{background:'#eff6ff',border:'1px solid #93c5fd',borderRadius:4,
                padding:'8px 12px',fontSize:12,color:'#1e40af'}}>
                💳 O recebimento em cartão vai para a <strong>Baixa de Cartões</strong>. A conta
                permanece aberta e será liquidada na baixa, quando o valor bruto entra na conta
                destino e a taxa é lançada na despesa.
              </div>
            ) : ehCreditoCli ? null : (
              <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12}}>
                Conta Financeira * {soBanco && <span style={{color:'#1e40af'}}>(somente contas do tipo banco)</span>}
                <select className={s.selectErp} value={f.conta_id} onChange={e=>set('conta_id',e.target.value)}>
                  <option value="">— Selecione —</option>
                  {contasFiltradas.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                {soBanco && contasFiltradas.length === 0 && (
                  <span style={{color:'#b91c1c',fontSize:11}}>
                    Nenhuma conta do tipo banco cadastrada — cadastre em Financeiro → Contas Financeiras.
                  </span>
                )}
              </label>
            )}
          </div>
        </div>
        <div className={s.confirmBtns}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={confirmar} disabled={salvando}>
            {salvando ? 'Confirmando...'
              : ehCartao ? 'Enviar para Baixa de Cartões'
              : fixo ? `Confirmar Recebimento de ${fmtMoeda(vLiquido)}`
              : parcial ? `Receber ${fmtMoeda(vReceb)} e gerar restante`
              : 'Confirmar Recebimento'}
          </button>
          <button className={s.btnErp} onClick={onClose} disabled={salvando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
